import { apiFetch } from "@/lib/jwt-auth-client";

export interface ChatAttachedImage {
  /** Hosted URL or local data URL used for the attachment preview. */
  url: string;
  /** Optional base64 data URL for vision (images only). */
  dataUrl?: string;
  filename: string;
  mediaType: string;
  type: "image" | "video";
}

export const CHAT_IMAGE_UPLOAD_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".svg",
]);

export const CHAT_VIDEO_UPLOAD_EXTENSIONS = new Set([".mp4", ".webm", ".mov", ".m4v"]);

export const CHAT_MAX_IMAGE_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
export const CHAT_MAX_VIDEO_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;

export const CHAT_IMAGE_UPLOAD_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";

export const CHAT_MEDIA_UPLOAD_ACCEPT = `${CHAT_IMAGE_UPLOAD_ACCEPT},video/mp4,video/webm,video/quicktime`;

const IMAGE_EXTENSION_LABEL = Array.from(CHAT_IMAGE_UPLOAD_EXTENSIONS).join(", ");
const MEDIA_EXTENSION_LABEL = [
  ...Array.from(CHAT_IMAGE_UPLOAD_EXTENSIONS),
  ...Array.from(CHAT_VIDEO_UPLOAD_EXTENSIONS),
].join(", ");

export function getChatAttachmentAllowedFormatsLabel(allowVideo: boolean): string {
  return allowVideo ? MEDIA_EXTENSION_LABEL : IMAGE_EXTENSION_LABEL;
}

export function formatChatAttachmentFailures(failures: string[], previewCount = 2): string | null {
  if (failures.length === 0) return null;
  const uniqueFailures = Array.from(new Set(failures));
  const preview = uniqueFailures.slice(0, previewCount).join(" ");
  const remaining = uniqueFailures.length - previewCount;
  const suffix = remaining > 0 ? ` (+${remaining} more)` : "";
  return `${preview}${suffix}`;
}

function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : "";
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function readUploadError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body?.error === "string" && body.error.trim()) {
      return body.error;
    }
  } catch {
    // Keep the fallback below.
  }
  return "Upload failed";
}

export async function uploadChatAttachmentFile(
  file: File,
  options: { allowVideo: boolean },
): Promise<{ attachment?: ChatAttachedImage; failure?: string }> {
  const ext = getFileExtension(file.name);
  const mime = (file.type || "").toLowerCase();
  const isImage = mime.startsWith("image/") || CHAT_IMAGE_UPLOAD_EXTENSIONS.has(ext);
  const isVideo =
    options.allowVideo && (mime.startsWith("video/") || CHAT_VIDEO_UPLOAD_EXTENSIONS.has(ext));

  if (!isImage && !isVideo) {
    return {
      failure: `${file.name}: unsupported type. Allowed formats: ${getChatAttachmentAllowedFormatsLabel(options.allowVideo)}.`,
    };
  }

  const maxSize = isVideo
    ? CHAT_MAX_VIDEO_UPLOAD_SIZE_BYTES
    : CHAT_MAX_IMAGE_UPLOAD_SIZE_BYTES;
  if (file.size > maxSize) {
    const maxLabel = isVideo ? "100MB" : "10MB";
    return { failure: `${file.name}: file is too large (max ${maxLabel}).` };
  }

  try {
    const dataUrl = isImage ? await readFileAsDataUrl(file) : undefined;
    const form = new FormData();
    form.append("file", file);
    const response = await apiFetch("/api/artifacts/upload-image/", {
      method: "POST",
      body: form,
    });

    if (!response.ok) {
      return { failure: `${file.name}: ${await readUploadError(response)}` };
    }

    const data = (await response.json()) as { url?: string; filename?: string };
    if (!data.url) {
      return { failure: `${file.name}: upload response missing URL` };
    }

    return {
      attachment: {
        url: data.url,
        dataUrl,
        filename: data.filename || file.name,
        mediaType: file.type || (isVideo ? "video/mp4" : "image/jpeg"),
        type: isVideo ? "video" : "image",
      },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Upload failed";
    return { failure: `${file.name}: ${message}` };
  }
}
